/**
 * Dashboard - Main post-login landing page
 * Pulls live stats from the demo API instead of mock data
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  TrendingUp,
  Clock,
  Target,
  BarChart3,
  FileText,
  ArrowRight,
  Calendar,
  Award,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Eye,
  Users,
  Building2,
  GitBranch,
  Briefcase,
  Sparkles,
  DoorOpen,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { useAuthStore } from '../store/authStore';
import DashboardLayout from '../components/DashboardLayout';
import LoadingSpinner from '../components/LoadingSpinner';
import dataService, { DashboardStats, AnalysisRecord, PlatformStats } from '../services/data';
import watchlistService, { WatchlistPayload } from '../services/watchlist';
import GroupIntelligencePanel from '../components/GroupIntelligencePanel';

const recommendationMap: Record<string, { text: string; color: string; icon: typeof CheckCircle2 }> = {
  strong_yes: {
    text: 'STRONG YES',
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: CheckCircle2
  },
  deal: {
    text: 'DEAL',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: AlertCircle
  },
  consider: {
    text: 'YES IF',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    icon: AlertCircle
  },
  pass: {
    text: 'PASS',
    color: 'bg-gray-100 text-gray-700 border-gray-200',
    icon: XCircle
  },
  pending: {
    text: 'ANALYZING...',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    icon: Clock
  }
};

const Dashboard: React.FC = () => {
  const { user } = useAuthStore();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [platform, setPlatform] = useState<PlatformStats | null>(null);
  const [recentCompanies, setRecentCompanies] = useState<any[]>([]);
  const [recentInvestors, setRecentInvestors] = useState<any[]>([]);
  const [recentAnalyses, setRecentAnalyses] = useState<AnalysisRecord[]>([]);
  const [watchlist, setWatchlist] = useState<WatchlistPayload | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadDashboard = async () => {
      setIsLoading(true);
      const [statsRes, platformRes] = await Promise.all([
        dataService.fetchDashboardStats(),
        dataService.fetchPlatformStats(),
      ]);
      if (statsRes.error) {
        // Old endpoint missing/empty is OK — platform stats are the
        // primary source now. Don't toast.
      } else if (statsRes.data) {
        setStats(statsRes.data);
        setRecentAnalyses(statsRes.data.recentAnalyses || []);
      }
      if (platformRes.error) {
        toast.error(platformRes.error);
      } else if (platformRes.data) {
        setPlatform(platformRes.data);
      }
      setIsLoading(false);
    };

    // Watchlist runs separately so a slow/missing endpoint doesn't
    // block the rest of the dashboard.
    const loadWatchlist = async () => {
      try {
        const payload = await watchlistService.fetchAll();
        setWatchlist(payload);
      } catch {
        // Endpoint not live yet on older deploys — silently fall back
        // to the placeholder count.
      }
    };

    // Recently-processed strips: top 8 companies + investors sorted by
    // last_researched_at DESC. Lets the operator focus on rows whose
    // assessment data is freshest, instead of commenting on stale rows.
    const loadRecent = async () => {
      try {
        const co = await dataService.fetchCompanies({
          size: 200,
          min_completion: 60,
        });
        if (co.data) {
          const sorted = [...co.data]
            .filter((c: any) => c.last_researched_at)
            .sort((a: any, b: any) => (b.last_researched_at || '').localeCompare(a.last_researched_at || ''))
            .slice(0, 8);
          setRecentCompanies(sorted);
        }
      } catch { /* silent */ }
      try {
        const resp = await fetch(
          (process.env.REACT_APP_API_URL || '/api/v1') + '/investors?size=200&min_completion=60',
          { headers: { 'Content-Type': 'application/json' } },
        );
        const data = await resp.json();
        const invs = (data.investors || [])
          .filter((i: any) => i.last_researched_at)
          .sort((a: any, b: any) => (b.last_researched_at || '').localeCompare(a.last_researched_at || ''))
          .slice(0, 8);
        setRecentInvestors(invs);
      } catch { /* silent */ }
    };

    loadDashboard();
    loadWatchlist();
    loadRecent();
  }, []);

  const getRecommendationBadge = (recommendation: string, score?: number | null) => {
    const mapping = recommendationMap[recommendation?.toLowerCase() as keyof typeof recommendationMap];
    if (!mapping) return null;

    const IconComponent = mapping.icon;

    return (
      <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${mapping.color}`}>
        <IconComponent className="w-3 h-3 mr-1" />
        {mapping.text}
        {score ? <span className="ml-1">({score}/100)</span> : null}
      </div>
    );
  };

  const getStatusIndicator = (status: string) => {
    if (status === 'in_progress') {
      return (
        <div className="flex items-center text-yellow-600">
          <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse mr-2"></div>
          <span className="text-sm">Analyzing</span>
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <LoadingSpinner />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Welcome Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Welcome back, {user?.firstName || 'there'}
            </h1>
            <p className="text-gray-600 mt-1">{user?.company}</p>
          </div>
          <Link
            to="/new-analysis"
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors inline-flex items-center shadow-sm"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Analysis
          </Link>
        </div>

        {/* Group Intelligence — cohort analytics for partner orgs */}
        <GroupIntelligencePanel />

        {/* High-level focus — Deal Rooms · Prediction · Matches */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/deal-rooms"
            className="bg-gradient-to-br from-amber-50 to-white rounded-xl border-2 border-amber-200 p-6 hover:shadow-lg transition-shadow group"
            title="Active deal rooms — investor diligence + introductions in progress"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 bg-amber-100 rounded-lg">
                <DoorOpen className="w-6 h-6 text-amber-700" />
              </div>
              <ArrowRight className="w-5 h-5 text-amber-300 group-hover:text-amber-700" />
            </div>
            <p className="text-4xl font-bold text-gray-900">{platform?.deals.active ?? '—'}</p>
            <p className="text-base font-semibold text-gray-800 mt-1">Active Deal Rooms</p>
            <div className="mt-4 space-y-1 text-xs text-gray-600">
              <div className="flex justify-between"><span>Total in pipeline</span><span className="font-medium text-gray-800">{platform?.deals.total ?? 0}</span></div>
              <div className="flex justify-between"><span>Term sheet</span><span className="font-medium text-gray-800">{platform?.deals.by_stage?.term_sheet ?? 0}</span></div>
              <div className="flex justify-between"><span>Closed</span><span className="font-medium text-gray-800">{platform?.deals.by_stage?.closed ?? 0}</span></div>
            </div>
          </Link>

          <Link
            to="/prediction-watchlist"
            className="bg-gradient-to-br from-purple-50 to-white rounded-xl border-2 border-purple-200 p-6 hover:shadow-lg transition-shadow group"
            title="What Aaron should watch today — macro-aware prediction model surfacing high-priority investors and companies"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 bg-purple-100 rounded-lg">
                <Sparkles className="w-6 h-6 text-purple-700" />
              </div>
              <ArrowRight className="w-5 h-5 text-purple-300 group-hover:text-purple-700" />
            </div>
            <p className="text-4xl font-bold text-gray-900">{watchlist?.total ?? '—'}</p>
            <p className="text-base font-semibold text-gray-800 mt-1">Prediction Watchlist</p>
            {watchlist && watchlist.total > 0 ? (
              <div className="mt-4 space-y-1 text-xs text-gray-600">
                <div className="flex justify-between"><span>Hot deals</span><span className="font-medium text-gray-800">{watchlist.deals?.length ?? 0}</span></div>
                <div className="flex justify-between"><span>Investors to engage</span><span className="font-medium text-gray-800">{watchlist.investors?.length ?? 0}</span></div>
                <div className="flex justify-between"><span>Companies to track</span><span className="font-medium text-gray-800">{watchlist.companies?.length ?? 0}</span></div>
              </div>
            ) : (
              <>
                <p className="text-xs text-gray-600 mt-3">
                  Macro-aware model surfacing the deals, investors, and companies most worth Aaron's attention right now.
                </p>
                <p className="text-xs text-purple-700 mt-2 font-medium">First daily generation pending</p>
              </>
            )}
          </Link>

          <Link
            to="/matches"
            className="bg-gradient-to-br from-blue-50 to-white rounded-xl border-2 border-blue-200 p-6 hover:shadow-lg transition-shadow group"
            title="Investor ↔ Startup matches — review and promote into deal rooms"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 bg-blue-100 rounded-lg">
                <GitBranch className="w-6 h-6 text-blue-700" />
              </div>
              <ArrowRight className="w-5 h-5 text-blue-300 group-hover:text-blue-700" />
            </div>
            <p className="text-4xl font-bold text-gray-900">{platform?.matches.high_score ?? 0}</p>
            <p className="text-base font-semibold text-gray-800 mt-1">High-Score Matches</p>
            <div className="mt-4 space-y-1 text-xs text-gray-600">
              <div className="flex justify-between"><span>Total generated</span><span className="font-medium text-gray-800">{platform?.matches.total ?? 0}</span></div>
              <div className="flex justify-between"><span>Pursuing</span><span className="font-medium text-gray-800">{platform?.matches.by_status?.pursuing ?? 0}</span></div>
              <div className="flex justify-between"><span>Introduced</span><span className="font-medium text-gray-800">{platform?.matches.by_status?.introduced ?? 0}</span></div>
            </div>
          </Link>
        </div>

        {/* Library totals — prominent platform-wide counts for
            companies + investors. Order matches the "Recently Processed"
            rows below (companies left, investors right). */}
        <div className="grid grid-cols-2 gap-4">
          <Link
            to="/companies"
            className="bg-gradient-to-br from-green-50 to-white rounded-xl border-2 border-green-200 p-6 hover:shadow-lg transition-shadow group"
            title="All companies on the platform"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 bg-green-100 rounded-lg">
                <Building2 className="w-6 h-6 text-green-700" />
              </div>
              <ArrowRight className="w-5 h-5 text-green-300 group-hover:text-green-700" />
            </div>
            <p className="text-4xl font-bold text-gray-900">{platform?.companies.total ?? '—'}</p>
            <p className="text-base font-semibold text-gray-800 mt-1">Total Companies</p>
            <div className="mt-4 space-y-1 text-xs text-gray-600">
              <div className="flex justify-between"><span>Researched</span><span className="font-medium text-gray-800">{platform?.companies.researched ?? 0}</span></div>
              <div className="flex justify-between"><span>Matched</span><span className="font-medium text-gray-800">{platform?.companies.matched ?? 0}</span></div>
              <div className="flex justify-between"><span>In a deal</span><span className="font-medium text-gray-800">{platform?.companies.with_deal ?? 0}</span></div>
            </div>
          </Link>
          <Link
            to="/investors"
            className="bg-gradient-to-br from-blue-50 to-white rounded-xl border-2 border-blue-200 p-6 hover:shadow-lg transition-shadow group"
            title="All investors on the platform"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-700" />
              </div>
              <ArrowRight className="w-5 h-5 text-blue-300 group-hover:text-blue-700" />
            </div>
            <p className="text-4xl font-bold text-gray-900">{platform?.investors.total ?? '—'}</p>
            <p className="text-base font-semibold text-gray-800 mt-1">Total Investors</p>
            <div className="mt-4 space-y-1 text-xs text-gray-600">
              <div className="flex justify-between"><span>Researched</span><span className="font-medium text-gray-800">{platform?.investors.researched ?? 0}</span></div>
              <div className="flex justify-between"><span>Matched</span><span className="font-medium text-gray-800">{platform?.investors.matched ?? 0}</span></div>
              <div className="flex justify-between"><span>With outreach</span><span className="font-medium text-gray-800">{platform?.investors.with_outreach ?? 0}</span></div>
            </div>
          </Link>
        </div>

        {/* Recently processed — top companies + investors by last_researched_at.
            Helps the operator avoid commenting on stale rows. */}
        {(recentCompanies.length > 0 || recentInvestors.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900">Recently Processed Companies</h2>
                <Link to="/companies" className="text-xs text-blue-700 hover:underline">View all →</Link>
              </div>
              <ul className="divide-y divide-gray-100">
                {recentCompanies.length === 0 ? (
                  <li className="px-5 py-4 text-xs text-gray-500">No recent rows yet.</li>
                ) : recentCompanies.map((c: any) => {
                  const age = (() => {
                    try {
                      const d = Math.floor((Date.now() - new Date(c.last_researched_at).getTime()) / (1000 * 60 * 60 * 24));
                      return d <= 0 ? 'today' : `${d}d ago`;
                    } catch { return ''; }
                  })();
                  return (
                    <li key={c.id} className="px-5 py-3 flex items-center justify-between hover:bg-gray-50">
                      <div className="min-w-0 flex-1">
                        <Link to={`/companies/${c.id}`} className="text-sm font-medium text-gray-900 hover:text-blue-700 truncate block">
                          {c.name || `Company #${c.id}`}
                        </Link>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {[c.industry, c.funding_stage].filter(Boolean).join(' · ') || '—'}
                        </p>
                      </div>
                      <div className="text-right ml-4 flex-shrink-0">
                        <span className="text-xs text-gray-600">{age}</span>
                        {c.research_completion_pct != null && (
                          <p className="text-xs font-semibold text-gray-700 mt-0.5">{c.research_completion_pct}%</p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="bg-white rounded-xl border border-gray-200">
              <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900">Recently Processed Investors</h2>
                <Link to="/investors" className="text-xs text-blue-700 hover:underline">View all →</Link>
              </div>
              <ul className="divide-y divide-gray-100">
                {recentInvestors.length === 0 ? (
                  <li className="px-5 py-4 text-xs text-gray-500">No recent rows yet.</li>
                ) : recentInvestors.map((i: any) => {
                  const age = (() => {
                    try {
                      const d = Math.floor((Date.now() - new Date(i.last_researched_at).getTime()) / (1000 * 60 * 60 * 24));
                      return d <= 0 ? 'today' : `${d}d ago`;
                    } catch { return ''; }
                  })();
                  return (
                    <li key={i.id} className="px-5 py-3 flex items-center justify-between hover:bg-gray-50">
                      <div className="min-w-0 flex-1">
                        <Link to={`/investors/${i.id}`} className="text-sm font-medium text-gray-900 hover:text-blue-700 truncate block">
                          {i.full_name || `Investor #${i.id}`}
                        </Link>
                        <p className="text-xs text-gray-500 mt-0.5">{i.firm_name || '—'}</p>
                      </div>
                      <div className="text-right ml-4 flex-shrink-0">
                        <span className="text-xs text-gray-600">{age}</span>
                        {i.research_completion_pct != null && (
                          <p className="text-xs font-semibold text-gray-700 mt-0.5">{i.research_completion_pct}%</p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}

        {/* Recent Analyses */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Recent Analyses</h2>
              <Link to="/analyses" className="text-sm font-medium text-blue-600 hover:text-blue-700 inline-flex items-center">
                View all
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {recentAnalyses.length === 0 ? (
              <div className="px-6 py-8 text-center text-sm text-gray-500">
                No analyses yet. Start a new one to see it here.
              </div>
            ) : (
              recentAnalyses.map((analysis) => (
                <div key={analysis.id} className="px-6 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{analysis.companyName}</p>
                    <p className="text-xs text-gray-500">
                      {[analysis.industry, analysis.fundingStage].filter(Boolean).join(' • ') || 'Stage TBD'}
                    </p>
                    <p className="text-xs text-gray-400">Created {new Date(analysis.createdAt).toLocaleString()}</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    {getRecommendationBadge(analysis.recommendation, analysis.score)}
                    {getStatusIndicator(analysis.status)}
                    <div className="text-sm text-gray-600 inline-flex items-center">
                      {analysis.score ? `${analysis.score}/100 score` : 'Score pending'}
                    </div>
                    <Link
                      to="/analyses"
                      className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700"
                    >
                      View report
                      <Eye className="w-4 h-4 ml-1" />
                    </Link>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Pipeline Snapshot */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-3">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Pipeline snapshot</h3>
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span className="inline-flex items-center"><CheckCircle2 className="w-4 h-4 text-green-500 mr-2" /> Deals recommended</span>
              <span>{recentAnalyses.filter((item) => item.recommendation === 'strong_yes' || item.recommendation === 'deal').length}</span>
            </div>
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span className="inline-flex items-center"><AlertCircle className="w-4 h-4 text-yellow-500 mr-2" /> Requires follow-up</span>
              <span>{recentAnalyses.filter((item) => item.recommendation === 'consider').length}</span>
            </div>
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span className="inline-flex items-center"><XCircle className="w-4 h-4 text-gray-500 mr-2" /> Passed</span>
              <span>{recentAnalyses.filter((item) => item.recommendation === 'pass').length}</span>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-3 text-sm text-gray-700">
            <h3 className="text-lg font-semibold text-gray-900">Analyst highlights</h3>
            <p className="flex items-start">
              <TrendingUp className="w-4 h-4 text-blue-600 mr-2 mt-0.5" />
              Portfolio confidence averages {recentAnalyses.length ? Math.round(recentAnalyses.reduce((total, item) => total + (item.confidence || 0), 0) / recentAnalyses.length) : 0}% across the latest cohort.
            </p>
            <p className="flex items-start">
              <Target className="w-4 h-4 text-green-600 mr-2 mt-0.5" />
              Market traction remains strongest in {recentAnalyses[0]?.industry || 'emerging categories'} this week.
            </p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
